var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TABLE_HIERARCHY_COLORS } from '../../../enum';
import { TableCell, TableRow } from '../../common';
import { ExpandableRowLabel } from '../../common/tableHierarchy';
import { useCurrencySymbol } from '../../hooks/useCurrencyOptions';
import { QueueType } from '../../multiskill/types';
import { useResourcingContext } from '../../report/hooks';
import { DisplayMode } from '../../report/resourcing';
import { useDisplayModeParams } from '../../report/selectors';
import { PLAN_COLORS } from '../../wizard/constants';
import { FirstCell, STICKIES } from '../annualTable';
import { CURRENCY, DECIMAL_VALUE, MULTISKILL_COLOR, NEST_LEVEL, PERCENTAGES } from '../constants';
import { generateVariances, useSelectCurrency } from '../selectors';
import { MultigeoCountryRow } from './multigeoQueuesByLob';
import { getMultiskillCategoryExpansionKey, getMultiskillGroupCountryExpansionKey, getMultiskillGroupExpansionKey } from './selectors';
import { getChunkBaselineValue, getHighlightBorder, getVarianceDecimalPlaces } from './utils';
var getGroupScenarioCountries = function (scenario, name) {
  var _a, _b, _c;
  return (_c = (_b = (_a = scenario.multiSkill) === null || _a === void 0 ? void 0 : _a.find(function (q) {
    return q.name === name;
  })) === null || _b === void 0 ? void 0 : _b.countries) !== null && _c !== void 0 ? _c : [];
};
var getGroupCountryValue = function (scenario, name, country, valueKey) {
  var _a, _b;
  return (_b = (_a = getGroupScenarioCountries(scenario, name).find(function (c) {
    return c.countryName === country.countryName;
  })) === null || _a === void 0 ? void 0 : _a[valueKey]) !== null && _b !== void 0 ? _b : null;
};
var getGroupLocationValue = function (scenario, name, country, locationName, valueKey) {
  var _a, _b, _c, _d;
  return (_d = (_c = (_b = (_a = getGroupScenarioCountries(scenario, name).find(function (c) {
    return c.countryName === country.countryName;
  })) === null || _a === void 0 ? void 0 : _a.locations) === null || _b === void 0 ? void 0 : _b.find(function (l) {
    return l.locationName === locationName;
  })) === null || _c === void 0 ? void 0 : _c[valueKey]) !== null && _d !== void 0 ? _d : null;
};
/** Union of countries/locations for a multiskill group across all scenarios **/
var mergeGroupCountries = function (data, name) {
  var byName = new Map();
  data.forEach(function (scenario) {
    getGroupScenarioCountries(scenario, name).forEach(function (country) {
      var _a, _b;
      var existing = byName.get(country.countryName);
      if (!existing) {
        byName.set(country.countryName, __assign(__assign({}, country), {
          locations: __spreadArray([], (_a = country.locations) !== null && _a !== void 0 ? _a : [], true)
        }));
        return;
      }
      var existingNames = new Set(existing.locations.map(function (l) {
        return l.locationName;
      }));
      ((_b = country.locations) !== null && _b !== void 0 ? _b : []).forEach(function (location) {
        if (!existingNames.has(location.locationName)) {
          existing.locations.push(__assign({}, location));
          existingNames.add(location.locationName);
        }
      });
    });
  });
  return Array.from(byName.values());
};
export var MultiskillGroupRows = function (_a) {
  var _b;
  var groupNames = _a.groupNames,
    data = _a.data,
    chunkSize = _a.chunkSize,
    valueKey = _a.valueKey,
    _c = _a.showVariance,
    showVariance = _c === void 0 ? false : _c,
    _d = _a.hideConstraintHeader,
    hideConstraintHeader = _d === void 0 ? false : _d,
    _e = _a.nestLevelOffset,
    nestLevelOffset = _e === void 0 ? 0 : _e,
    expandedItems = _a.expandedItems,
    onToggleItem = _a.onToggleItem;
  var t = useTranslation('report').t;
  var isPercentages = useResourcingContext().isPercentages;
  var displayMode = useDisplayModeParams()[0];
  var currency = useSelectCurrency();
  var isCurrencyRow = CURRENCY.includes(valueKey);
  var _f = useCurrencySymbol(isCurrencyRow ? currency : undefined),
    currencyPrefix = _f.prefix,
    currencySuffix = _f.suffix;
  var varianceOptions = {
    decimalPlaces: getVarianceDecimalPlaces(valueKey, isCurrencyRow),
    currencyPrefix: currencyPrefix,
    currencySuffix: currencySuffix
  };
  if (!groupNames.length) return null;
  var categoryExpansionKey = getMultiskillCategoryExpansionKey();
  var categoryExpanded = hideConstraintHeader || (onToggleItem ? (_b = expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[categoryExpansionKey]) !== null && _b !== void 0 ? _b : false : true);
  return _jsxs(_Fragment, {
    children: [!hideConstraintHeader && _jsxs(TableRow, {
      children: [_jsx(FirstCell, __assign({
        "aria-label": NEST_LEVEL[2 - nestLevelOffset],
        stickySizes: STICKIES,
        color: TABLE_HIERARCHY_COLORS.lightBlueGray
      }, {
        children: _jsx(Box, __assign({
          fontWeight: "bold"
        }, {
          children: _jsx(ExpandableRowLabel, __assign({
            expanded: categoryExpanded,
            onToggle: onToggleItem ? function () {
              return onToggleItem(categoryExpansionKey);
            } : undefined,
            bold: true
          }, {
            children: t('annual.reportRows.multiSkill')
          }))
        }))
      })), data.map(function (_, index, arr) {
        return _jsx(TableCell, {
          colSpan: 2,
          color: TABLE_HIERARCHY_COLORS.lightBlueGray,
          highlightBorder: getHighlightBorder(index, arr.length, chunkSize)
        }, "multiskill-cat-".concat(valueKey, "-").concat(index));
      })]
    }), categoryExpanded && groupNames.map(function (name) {
      var _a, _b, _c, _d;
      // Collect this group's value per scenario for variance baseline
      var scenarioValues = data.map(function (scenario) {
        var _a, _b;
        var group = (_a = scenario.multiSkill) === null || _a === void 0 ? void 0 : _a.find(function (q) {
          return q.name === name;
        });
        return group ? (_b = group[valueKey]) !== null && _b !== void 0 ? _b : null : null;
      });
      var group = (_b = (_a = data.find(function (scenario) {
        var _a;
        return (_a = scenario.multiSkill) === null || _a === void 0 ? void 0 : _a.some(function (q) {
          return q.name === name;
        });
      })) === null || _a === void 0 ? void 0 : _a.multiSkill) === null || _b === void 0 ? void 0 : _b.find(function (q) {
        return q.name === name;
      });
      var countries = mergeGroupCountries(data, name);
      var groupId = (_c = group === null || group === void 0 ? void 0 : group.group) !== null && _c !== void 0 ? _c : null;
      var groupExpansionKey = getMultiskillGroupExpansionKey(name);
      var expanded = onToggleItem ? (_d = expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[groupExpansionKey]) !== null && _d !== void 0 ? _d : false : true;
      var hasCountries = !!countries.length;
      return _jsxs(React.Fragment, {
        children: [_jsxs(TableRow, {
          children: [_jsx(FirstCell, __assign({
            "aria-label": NEST_LEVEL[3 - nestLevelOffset],
            stickySizes: STICKIES,
            bold: true,
            sx: {
              backgroundColor: MULTISKILL_COLOR
            }
          }, {
            children: _jsx(ExpandableRowLabel, __assign({
              expanded: expanded,
              onToggle: hasCountries && onToggleItem ? function () {
                return onToggleItem(groupExpansionKey);
              } : undefined,
              bold: true
            }, {
              children: name
            }))
          })), scenarioValues.map(function (value, index, arr) {
            var _a, _b, _c;
            var scenario = data[index];
            var baseValue = getChunkBaselineValue(scenarioValues, index, chunkSize);
            var isGroupedMultiskillScenario = scenario.dataType === QueueType.Multiskill && !!((_a = scenario.multiSkill) === null || _a === void 0 ? void 0 : _a.some(function (q) {
              return q.name === name;
            }));
            return _jsx(TableCell, {
              colSpan: 2,
              value: value,
              decimal: (_b = DECIMAL_VALUE[valueKey]) !== null && _b !== void 0 ? _b : 0,
              percentage: PERCENTAGES.includes(valueKey),
              bold: true,
              currency: isCurrencyRow ? currency : undefined,
              color: isGroupedMultiskillScenario ? MULTISKILL_COLOR : (_c = PLAN_COLORS[scenario.colorIndex]) === null || _c === void 0 ? void 0 : _c[1],
              useColorValue: isGroupedMultiskillScenario,
              highlightBorder: getHighlightBorder(index, arr.length, chunkSize),
              expandchildren: showVariance && displayMode !== DisplayMode.All ? generateVariances(baseValue !== null && baseValue !== void 0 ? baseValue : null, value, isPercentages, varianceOptions) : undefined
            }, "multiskill-".concat(valueKey, "-").concat(name, "-").concat(index));
          })]
        }), expanded && countries.map(function (country) {
          var countryExpansionKey = getMultiskillGroupCountryExpansionKey(name, country.countryName);
          var scenarioCountryValues = data.map(function (scenario) {
            return getGroupCountryValue(scenario, name, country, valueKey);
          });
          var scenarioLocationValues = country.locations.map(function (location) {
            return data.map(function (scenario) {
              return getGroupLocationValue(scenario, name, country, location.locationName, valueKey);
            });
          });
          // Per-scenario membership: null when this group is absent from the scenario,
          // so country/location cells only highlight in scenarios owning the group.
          var scenarioGroups = data.map(function (scenario) {
            var _a, _b, _c;
            return (_c = (_b = (_a = scenario.multiSkill) === null || _a === void 0 ? void 0 : _a.find(function (q) {
              return q.name === name;
            })) === null || _b === void 0 ? void 0 : _b.group) !== null && _c !== void 0 ? _c : null;
          });
          return _jsx(MultigeoCountryRow, {
            country: country,
            scenarioValues: scenarioCountryValues,
            scenarioLocationValues: scenarioLocationValues,
            data: data,
            chunkSize: chunkSize,
            keyItem: valueKey,
            isMultiskillQueue: true,
            group: groupId,
            scenarioGroups: scenarioGroups,
            expandedProp: expandedItems === null || expandedItems === void 0 ? void 0 : expandedItems[countryExpansionKey],
            onToggle: onToggleItem ? function () {
              return onToggleItem(countryExpansionKey);
            } : undefined,
            nestLevelOffset: nestLevelOffset
          }, "multiskill-country-".concat(name, "-").concat(country.countryName));
        })]
      }, name);
    })]
  });
};